import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Phone, CheckCircle, XCircle, Buildings } from "@phosphor-icons/react";
import { useSeo } from "@/lib/useSeo";
import QuoteRequestForm from "@/components/QuoteRequestForm";
import { MobileActionBar } from "@/components/MobileActionBar";
import { SiteHeader, PHONE_DISPLAY, PHONE_HREF } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

const NAVY = "#182848";
const ORANGE = "#EE7623";
const SLATE = "#4A5A72";
const CREAM = "#FDF3EA";
const WHITE = "#FFFFFF";
const LINE = "rgba(24,40,72,0.14)";
const URL = "https://hcobnetwork.com/landlord-turnover";
export const TURNOVER_SERVICE_LABEL = "Rental turnover / eviction cleanout";

const LOADS = [
  { size: "Minimum load", holds: "A few items", price: 125 },
  { size: "Quarter load", holds: "A small room's worth", price: 225 },
  { size: "Half load", holds: "A garage bay or a full bedroom", price: 375 },
  { size: "Three-quarter load", holds: "A packed basement", price: 525 },
  { size: "Full load", holds: "A full garage or small apartment", price: 695 },
];

const FAQ = [
  ["How much does an eviction cleanout cost in Baltimore?", "It depends on how much was left. Our junk removal loads run from $125 to $695 each, and most units need one to a few loads. We quote from photos, and if you add cleaning, paint or repairs, it's all in one quote."],
  ["How fast can you turn a unit?", "It depends on the scope. A cleanout and clean can often be done in a day; paint and repairs add time. We give you a rent-ready date with the quote."],
  ["Do I need to be there?", "No. Send photos and a lockbox code or key, and we'll handle it and keep you updated."],
  ["When can you remove a former tenant's belongings?", "Only once the unit is legally clear for removal under Maryland and Baltimore City rules. Confirm with your attorney if you're unsure; we start when you give us the go-ahead."],
  ["Can you do the painting and repairs too?", "Yes. Painting, drywall, flooring and handyman repairs are part of the same job, sequenced so the cleaning happens last."],
  ["Do you work with property management companies?", "Yes. We set up recurring turnover programs with one point of contact for every unit. See our property manager page.", "/property-managers"],
  ["What won't you take?", "Paint, fuel, chemicals, asbestos and other hazardous materials. We'll tell you who handles them."],
];

export const LANDLORD_TURNOVER_SEO = {
  title: "Eviction Cleanouts & Rental Turnovers Baltimore | HCOB",
  description: "Eviction cleanouts and rental turnovers in Baltimore. We clear it, clean it, paint and repair it, and hand it back rent-ready — one vendor, one invoice. Loads from $125.",
  path: "/landlord-turnover",
  og: {
    "og:title": "Eviction Cleanouts & Rental Turnovers Baltimore | HCOB",
    "og:site_name": "HCOB Property Services",
    "og:description": "Clear it, clean it, paint and repair it, hand it back rent-ready — one vendor, one invoice. Loads from $125.",
    "og:url": URL,
    "og:type": "website",
  },
  jsonLd: {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "LocalBusiness", "@id": "https://hcobnetwork.com/#localbusiness", name: "HCOB Property Services", telephone: "+1-410-870-9347", url: "https://hcobnetwork.com" },
      {
        "@type": "Service",
        "@id": `${URL}#service`,
        name: "Eviction Cleanouts & Rental Turnovers",
        serviceType: "Eviction cleanout and rental turnover",
        url: URL,
        description: "Eviction and abandonment cleanouts, tenant trash-outs, move-out deep cleaning, carpet, paint and repairs run as one managed rental turnover across Baltimore City, Baltimore County and Howard County, Maryland.",
        provider: { "@id": "https://hcobnetwork.com/#localbusiness" },
        areaServed: [
          { "@type": "City", name: "Baltimore" },
          { "@type": "AdministrativeArea", name: "Baltimore County" },
          { "@type": "AdministrativeArea", name: "Howard County" },
          { "@type": "State", name: "Maryland" },
        ],
        offers: LOADS.map((l) => ({ "@type": "Offer", name: `Junk removal — ${l.size}`, description: l.holds, price: String(l.price), priceCurrency: "USD", url: `${URL}#pricing` })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://hcobnetwork.com/" },
          { "@type": "ListItem", position: 2, name: "Property Managers", item: "https://hcobnetwork.com/property-managers" },
          { "@type": "ListItem", position: 3, name: "Rental Turnovers", item: URL },
        ],
      },
      { "@type": "FAQPage", mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
    ],
  },
};

const NAV_ANCHORS = [
  { href: "#pricing", label: "Pricing" },
  { href: "#process", label: "How it works" },
  { href: "#quote", label: "Get a Turnover Quote", primary: true },
];

const INCLUDED = [
  { title: "Clear it out", items: ["Eviction and abandonment cleanouts", "Tenant move-out trash-outs: furniture, mattresses, bagged trash, basement and yard debris", "Appliance removal"], img: { src: "/landlord-turnover/img/baltimore-empty-rental-bedroom-hardwood-floor-after-cleaning.webp", alt: "Emptied Baltimore rental bedroom with bare hardwood floor after a cleanout and cleaning" } },
  { title: "Clean it", items: [<><a href="/move-out-cleaning" data-testid="lt-move-out-link" className="font-semibold underline underline-offset-2" style={{ color: NAVY }}>Move-out deep cleaning</a>: inside cabinets, inside appliances, bathrooms, every floor</>, "Carpet cleaning, carpet removal and odor treatment"], img: { src: "/landlord-turnover/img/baltimore-empty-rental-kitchen-after-cleaning.webp", alt: "Empty Baltimore rental kitchen with wiped cabinets and cleaned floor after a turnover cleaning" } },
  { title: "Fix it", items: ["Painting", "Drywall patching", "Flooring", "Handyman repairs", "Pressure washing and landscaping for curb appeal"], img: { src: "/landlord-turnover/img/baltimore-empty-rental-living-room-brick-wall-after-cleaning.webp", alt: "Empty Baltimore rental living room with exposed brick, finished walls and cleaned laminate floor after a turnover" } },
];

const STEPS = [
  ["Send photos and access", "Photos of each room, plus a lockbox code or key handoff. No need to meet us there."],
  ["Get one quote for the whole job", "Cleanout, cleaning, carpet, paint and repairs, scoped and priced together."],
  ["We sequence the trades", "Haul first, then repairs and paint, then deep clean and carpet last, so nothing gets cleaned twice."],
  ["Tracked from start to finish", "Crews clock in with GPS verification and work from a checklist built for your unit. One coordinator keeps you updated."],
  ["Rent-ready hand-back", "You get the keys back to a unit that's ready to photograph and list, and one invoice."],
];

const COMPARISON = [
  ["Who shows up", "Vetted, managed local crews", "Whoever bought the lead"],
  ["Trades", "Cleanout, cleaning, carpet, paint, repairs in one job", "One trade per hire"],
  ["Coordination", "We sequence the trades", "You do"],
  ["Accountability", "GPS-verified, platform-tracked work", "Hope they show up"],
  ["Billing", "One quote, one invoice", "One per contractor"],
];

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } } };

const Eyebrow = ({ children, light }) => (
  <div className="font-mono-label text-[10px] tracking-[0.28em]" style={{ color: light ? "rgba(255,255,255,0.7)" : ORANGE }}>{children}</div>
);
const H2 = ({ children, testid, light }) => (
  <h2 className="font-display mt-4 font-black leading-[1.02] tracking-tighter" style={{ color: light ? WHITE : NAVY, fontSize: "clamp(28px, 3.6vw, 46px)" }} data-testid={testid}>{children}</h2>
);
const Section = ({ id, testid, bg = WHITE, children, border = true }) => (
  <section id={id} data-testid={testid} className={border ? "border-b" : ""} style={{ backgroundColor: bg, borderColor: LINE }}>
    <div className="mx-auto max-w-[1400px] px-5 py-14 md:px-10 md:py-20">{children}</div>
  </section>
);
const Prose = ({ children }) => <div className="mt-6 max-w-[760px] space-y-4 text-[15px] leading-[1.75] md:text-base" style={{ color: SLATE }}>{children}</div>;

export default function LandlordTurnover() {
  useSeo(LANDLORD_TURNOVER_SEO);
  return (
    <div data-testid="landlord-turnover-page" className="min-h-screen antialiased" style={{ backgroundColor: WHITE, color: NAVY }}>
      <SiteHeader anchors={NAV_ANCHORS} brandTestId="lt-brand" />

      {/* Hero */}
      <section id="top" data-testid="lt-hero" style={{ backgroundColor: NAVY, color: WHITE }}>
        <motion.div initial="hidden" animate="show" variants={stagger} className="mx-auto max-w-[1400px] px-5 py-14 md:px-10 md:py-20">
          <motion.div variants={fadeUp} className="font-mono-label flex items-center gap-2 text-[10px] tracking-[0.26em] text-white/70" data-testid="lt-label">
            <Buildings size={14} weight="duotone" style={{ color: ORANGE }} /> For landlords &amp; property managers · Baltimore &amp; Baltimore County
          </motion.div>
          <motion.h1 data-testid="lt-h1" variants={fadeUp} className="font-display mt-8 max-w-[18ch] text-4xl font-black leading-[0.98] tracking-tighter sm:text-5xl lg:text-6xl">
            Eviction Cleanouts &amp; Rental Turnovers in <span style={{ color: ORANGE }}>Baltimore, MD</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-7 max-w-[720px] text-[15px] leading-[1.65] text-white/85 md:text-base" data-testid="lt-answer">
            HCOB Property Services turns rental units across Baltimore City, Baltimore County and Howard County: we clear out what the last tenant left, deep clean, treat or replace <a href="/carpet-cleaning" data-testid="lt-carpet-link" className="font-bold underline underline-offset-4" style={{ color: ORANGE }}>carpet</a>, paint, handle repairs, and hand the unit back rent-ready. One quote, one invoice, one point of contact instead of three vendors. Junk removal loads start at $125, and we quote from photos.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a href="#quote" data-testid="lt-hero-cta" className="inline-flex h-14 items-center justify-center gap-2 rounded-sm px-7 text-[14px] font-black text-white" style={{ backgroundColor: ORANGE }}>
              Get a Turnover Quote <ArrowRight size={16} weight="bold" />
            </a>
            <a href={PHONE_HREF} data-testid="lt-hero-call" className="inline-flex h-14 items-center justify-center gap-2 rounded-sm border border-white/30 px-6 text-[13px] font-bold text-white hover:bg-white/10">
              <Phone size={16} weight="fill" /> Call or Text {PHONE_DISPLAY}
            </a>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-8 font-mono-label text-[10px] tracking-[0.2em] text-white/80" data-testid="lt-trust-strip">
            One vendor for every trade &nbsp;•&nbsp; GPS-verified arrival, platform-tracked work &nbsp;•&nbsp; Rent-ready hand-back
          </motion.div>
          <motion.p variants={fadeUp} className="mt-5 text-[14px] text-white/80">
            Need it today? <a href="/same-day-cleaning" data-testid="need-it-today" className="font-bold underline underline-offset-4" style={{ color: ORANGE }}>Same-day turnover cleaning in Baltimore — request by noon.</a>
          </motion.p>
        </motion.div>
      </section>

      {/* The turnover problem */}
      <Section testid="lt-problem">
        <Eyebrow>01 · The problem</Eyebrow>
        <H2>The turnover problem</H2>
        <Prose>
          <p>Every day a unit sits empty costs you rent. And the turnover is never just one job: the trash-out reveals the carpet, the carpet reveals the floor, and the walls always need paint.</p>
          <p>With most vendors that's a hauler, a cleaner, a carpet company and a painter — four schedules, four invoices, and you coordinating the order so nobody cleans before the painter's done.</p>
          <p className="border-l-2 pl-4 font-display text-lg font-bold" style={{ borderColor: ORANGE, color: NAVY }}>We run it as one managed job. We scope everything at once, sequence the trades in the right order, and hand you back a unit that's ready to list.</p>
        </Prose>
      </Section>

      {/* What's included */}
      <Section id="included" testid="lt-included" bg={CREAM}>
        <Eyebrow>02 · Scope</Eyebrow>
        <H2>What's included</H2>
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {INCLUDED.map((g) => (
            <div key={g.title} data-testid={`lt-included-${g.title.toLowerCase().replace(/\s+/g, "-")}`} className="overflow-hidden rounded-sm border bg-white" style={{ borderColor: LINE }}>
              <img src={g.img.src} alt={g.img.alt} width="1200" height="900" loading="lazy" decoding="async" className="block aspect-[4/3] w-full object-cover" />
              <div className="p-6">
                <div className="font-display text-xl font-black tracking-tight" style={{ color: NAVY }}>{g.title}</div>
                <ul className="mt-4 space-y-3">
                  {g.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-3 text-[14px] leading-[1.55]" style={{ color: SLATE }}>
                      <CheckCircle size={18} weight="fill" className="mt-[2px] shrink-0" style={{ color: NAVY }} /><span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-start gap-3 rounded-sm border bg-white p-5 text-[14px]" style={{ borderColor: LINE, color: SLATE }} data-testid="lt-not-included">
          <XCircle size={18} weight="fill" className="mt-[2px] shrink-0" style={{ color: "rgba(74,90,114,0.6)" }} />
          <div><span className="font-bold" style={{ color: NAVY }}>Not included:</span> paint, fuel, chemicals, asbestos and other hazardous materials. We'll point you to who handles them.</div>
        </div>
      </Section>

      {/* Pricing */}
      <Section id="pricing" testid="lt-pricing">
        <Eyebrow>03 · Pricing</Eyebrow>
        <H2>Turnover pricing</H2>
        <Prose><p>We quote each turnover from photos as one job, so you see the whole number up front. Here's how the pieces are priced:</p></Prose>
        <Prose><p>Junk removal is priced by volume at the same published load rates as our <a href="/junk-removal" data-testid="lt-junk-link" className="font-semibold underline underline-offset-2" style={{ color: NAVY }}>junk removal page</a>. Most single-unit trash-outs land between one and three loads; we tell you how many when we quote, not after the truck fills up.</p></Prose>
        <div className="mt-8 overflow-hidden rounded-sm border" style={{ borderColor: LINE }}>
          <div className="px-5 py-3 font-mono-label text-[10px] tracking-[0.26em] text-white" style={{ backgroundColor: NAVY }}>Junk removal · published load prices</div>
          <table className="w-full border-collapse text-[14px]" data-testid="lt-price-table">
            <thead>
              <tr className="text-left font-mono-label text-[10px] tracking-[0.22em]" style={{ color: SLATE, backgroundColor: "rgba(24,40,72,0.05)" }}>
                <th scope="col" className="px-5 py-3 font-bold">Load size</th>
                <th scope="col" className="px-5 py-3 font-bold">What it holds</th>
                <th scope="col" className="px-5 py-3 text-right font-bold">Price</th>
              </tr>
            </thead>
            <tbody>
              {LOADS.map((l) => (
                <tr key={l.size} className="border-t" style={{ borderColor: LINE }} data-testid={`lt-price-row-${l.price}`}>
                  <td className="px-5 py-4 font-display font-bold" style={{ color: NAVY }}>{l.size}</td>
                  <td className="px-5 py-4" style={{ color: SLATE }}>{l.holds}</td>
                  <td className="px-5 py-4 text-right font-display font-black" style={{ color: ORANGE }}>${l.price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="mt-6 max-w-[760px] space-y-3 text-[14px] leading-[1.6]" style={{ color: SLATE }} data-testid="lt-pricing-notes">
          {[
            <>Move-out deep cleaning: by bedrooms and bathrooms (<a href="/move-out-cleaning" data-testid="lt-moveout-prices-link" className="font-semibold underline underline-offset-2" style={{ color: NAVY }}>see prices on our move-out cleaning page</a>)</>,
            "Carpet, paint, drywall, flooring and repairs: quoted from photos as part of the same job",
            "Stairs are included in junk removal — no extra charge for a basement or third-floor unit",
            "Prices plus Maryland sales tax where applicable",
          ].map((t, i) => (
            <li key={i} className="flex items-start gap-3"><span className="mt-[9px] inline-block h-[6px] w-6 shrink-0" style={{ backgroundColor: ORANGE }} /><span>{t}</span></li>
          ))}
        </ul>
        <p className="mt-6 text-[15px] font-semibold" style={{ color: NAVY }}>Managing several units? Ask about <a href="/property-managers" data-testid="lt-recurring-link" className="underline underline-offset-2" style={{ color: ORANGE }}>recurring turnover programs</a> for portfolios.</p>
        <a href="#quote" data-testid="lt-pricing-cta" className="mt-8 inline-flex h-14 items-center justify-center gap-2 rounded-sm px-7 text-[14px] font-black text-white" style={{ backgroundColor: ORANGE }}>
          Send Photos for a Turnover Quote <ArrowRight size={16} weight="bold" />
        </a>
      </Section>

      {/* How a turnover works */}
      <Section id="process" testid="lt-process" bg={CREAM}>
        <Eyebrow>04 · How it works</Eyebrow>
        <H2>How a turnover works</H2>
        <motion.ol initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-5">
          {STEPS.map(([t, b], i) => (
            <motion.li key={t} variants={fadeUp} data-testid={`lt-step-${i + 1}`} className="rounded-sm border bg-white p-6" style={{ borderColor: LINE }}>
              <div className="font-display text-[44px] font-black leading-none tracking-tighter" style={{ color: ORANGE }}>{String(i + 1).padStart(2, "0")}</div>
              <div className="font-display mt-4 text-[17px] font-black leading-tight tracking-tight" style={{ color: NAVY }}>{t}</div>
              <p className="mt-2 text-[13.5px] leading-[1.6]" style={{ color: SLATE }}>{b}</p>
            </motion.li>
          ))}
        </motion.ol>
      </Section>

      {/* Eviction know-how */}
      <Section testid="lt-eviction">
        <Eyebrow>05 · Local know-how</Eyebrow>
        <H2>Eviction cleanouts: what to know</H2>
        <Prose>
          <p><span className="font-bold" style={{ color: NAVY }}>Wait until the unit is legally clear to clear.</span> Maryland and Baltimore City rules control when and how a former tenant's belongings can be removed after an eviction. We start once you confirm the unit is cleared for removal — check with your attorney if you're unsure.</p>
          <p>Anything that looks personal or important — IDs, documents, photos — is bagged and set aside for you, not thrown out.</p>
          <p><span className="font-bold" style={{ color: NAVY }}>City and county bulk pickup won't cover a turnover.</span> Baltimore City collects up to 3 bulk items a month and won't take construction debris, and Baltimore County's twice-yearly bulk collection only covers single-family homes and townhomes. A trash-out plus renovation debris needs a hauler.</p>
          <p><span className="font-bold" style={{ color: NAVY }}>Rowhome rentals take planning.</span> Narrow stairs, basement units, no driveway and alley-only access are normal for Baltimore rentals. Our crews are local and plan for them.</p>
          <p className="text-[12.5px]" data-testid="lt-bulk-note">City and County rules as of October 2026. Check 311 (City) or baltimorecountymd.gov (County) for current details.</p>
        </Prose>
      </Section>

      {/* Who we work with */}
      <Section testid="lt-who" bg={CREAM}>
        <Eyebrow>06 · Who we work with</Eyebrow>
        <H2>Who we work with</H2>
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {[
            ["Independent landlords", "One unit or ten, you get one point of contact and one invoice per turnover. Send photos from wherever you are and get the keys back to a unit that's ready to list."],
            ["Property management companies", <>Multi-unit and <a href="/property-managers" data-testid="lt-who-recurring-link" className="font-semibold underline underline-offset-2" style={{ color: NAVY }}>recurring programs</a> with a turnover checklist set up for your portfolio. Your coordinators deal with one HCOB contact, not a rotating list of subcontractors.</>],
            ["Realtors and investors", "Clear, clean and repair a property before listing or after closing. One scope covers the trash-out, the deep clean and the cosmetic repairs that show up in listing photos."],
          ].map(([t, b]) => (
            <div key={t} className="rounded-sm border bg-white p-6" style={{ borderColor: LINE, borderLeft: `4px solid ${ORANGE}` }}>
              <div className="font-display text-lg font-black tracking-tight" style={{ color: NAVY }}>{t}</div>
              <p className="mt-2 text-[14px] leading-[1.6]" style={{ color: SLATE }}>{b}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Comparison */}
      <Section id="comparison" testid="lt-comparison">
        <Eyebrow>07 · The comparison</Eyebrow>
        <H2>Why landlords switch from lead-gen sites</H2>
        <div className="mt-10 overflow-hidden rounded-sm border bg-white" style={{ borderColor: LINE }}>
          <div className="grid grid-cols-12 border-b" style={{ borderColor: LINE }}>
            <div className="col-span-3 px-3 py-4 md:px-6" />
            <div className="col-span-5 px-3 py-4 text-white md:px-6" style={{ backgroundColor: NAVY }}>
              <div className="font-display text-[13px] font-bold">HCOB Property Services</div>
            </div>
            <div className="col-span-4 px-3 py-4 md:px-6">
              <div className="font-display text-[13px] font-bold" style={{ color: SLATE }}>Lead-gen sites (Angi, Thumbtack)</div>
            </div>
          </div>
          {COMPARISON.map(([dim, us, them], i) => (
            <div key={dim} data-testid={`lt-compare-${dim.toLowerCase().replace(/\s+/g, "-")}`} className="grid grid-cols-12" style={{ borderTop: i ? `1px solid ${LINE}` : "none" }}>
              <div className="col-span-3 px-3 py-5 md:px-6"><div className="font-display text-[13px] font-bold tracking-tight md:text-[15px]" style={{ color: NAVY }}>{dim}</div></div>
              <div className="col-span-5 flex items-start gap-2 px-3 py-5 md:gap-3 md:px-6" style={{ backgroundColor: "rgba(24,40,72,0.05)" }}>
                <CheckCircle size={18} weight="fill" className="mt-[2px] shrink-0" style={{ color: NAVY }} />
                <div className="text-[13px] leading-[1.5] md:text-[15px]" style={{ color: NAVY }}>{us}</div>
              </div>
              <div className="col-span-4 flex items-start gap-2 px-3 py-5 md:gap-3 md:px-6">
                <XCircle size={18} weight="fill" className="mt-[2px] shrink-0" style={{ color: "rgba(74,90,114,0.5)" }} />
                <div className="text-[13px] leading-[1.5] md:text-[14px]" style={{ color: SLATE }}>{them}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Areas */}
      <Section testid="lt-areas" bg={CREAM}>
        <Eyebrow>08 · Service area</Eyebrow>
        <H2>Areas we serve</H2>
        <Prose>
          <p data-testid="lt-areas-copy">We're based in Gwynn Oak and work Baltimore City, Baltimore County and Howard County every day — including Pikesville, Catonsville, Randallstown, Windsor Mill and Ellicott City — and take jobs across all of Maryland. Check your ZIP on our <a href="/service-area" data-testid="lt-service-area-link" className="font-semibold underline underline-offset-2" style={{ color: NAVY }}>service area page</a>.</p>
          <p>Rowhome, garden apartment, converted single-family or small multi-unit building — the process is the same: photos in, one quote back, one crew on site, one invoice when the unit is rent-ready.</p>
        </Prose>
      </Section>

      {/* FAQ */}
      <Section id="faq" testid="lt-faq">
        <Eyebrow>09 · Questions</Eyebrow>
        <H2>Eviction cleanout and turnover questions</H2>
        <div className="mt-8 max-w-[860px] space-y-2">
          {FAQ.map(([q, a, href], i) => (
            <details key={q} data-testid={`lt-faq-${i + 1}`} className="group rounded-sm border bg-white px-5 py-4" style={{ borderColor: LINE }}>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display text-[15px] font-bold" style={{ color: NAVY }}>
                {q}<span className="font-display text-[22px] font-light leading-none transition-transform group-open:rotate-45" style={{ color: ORANGE }} aria-hidden>+</span>
              </summary>
              <p className="mt-3 text-[14px] leading-[1.65]" style={{ color: SLATE }}>
                {href ? <>{a.replace(/See our property manager page\.$/, "")}<a href={href} data-testid="lt-faq-pm-link" className="font-semibold underline underline-offset-2" style={{ color: NAVY }}>See our property manager page.</a></> : a}
              </p>
            </details>
          ))}
        </div>
      </Section>

      {/* Quote form */}
      <section id="quote" data-testid="lt-quote" style={{ backgroundColor: WHITE }}>
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 py-14 md:grid-cols-12 md:px-10 md:py-20">
          <div className="md:col-span-5">
            <Eyebrow>10 · Get a quote</Eyebrow>
            <H2>Send photos. Get one quote for the whole turnover.</H2>
            <p className="mt-6 text-[15px] leading-relaxed" style={{ color: SLATE }} data-testid="lt-quote-sub">
              Tell us the address, unit size and what you're seeing. We'll text back a scope and price, usually the same day.
            </p>
            <ul className="mt-8 space-y-4">
              {["Cleanout, cleaning, carpet, paint and repairs in one quote", "Lockbox or key handoff — no need to meet us there", "Rent-ready hand-back and one invoice"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[14px]" style={{ color: NAVY }}>
                  <span className="mt-[9px] inline-block h-[6px] w-8 shrink-0" style={{ backgroundColor: ORANGE }} />{t}
                </li>
              ))}
            </ul>
            <p className="mt-8 text-[14px]" style={{ color: SLATE }} data-testid="lt-quote-alt">Or call or text <a href={PHONE_HREF} className="font-bold" style={{ color: NAVY }}>{PHONE_DISPLAY}</a>.</p>
          </div>
          <div className="order-first md:order-none md:col-span-7">
            <div className="rounded-sm border bg-white p-4 md:p-6" style={{ borderColor: LINE }}>
              <QuoteRequestForm
                fixedService={TURNOVER_SERVICE_LABEL}
                eyebrow="Turnover quote"
                title="Tell us about the unit."
                messageLabel="What are you seeing? (unit size, what was left behind, paint or repairs needed…)"
                messagePlaceholder="2BR rowhome, tenant left furniture and bagged trash in the basement, walls need paint. Lockbox on site."
              />
            </div>
          </div>
        </div>
      </section>

      <SiteFooter quoteHref="#quote" />
      <MobileActionBar primaryLabel="Get a Turnover Quote" primaryHref="#quote" phoneHref={PHONE_HREF} textBody="Hi HCOB Property Services — I need a rental turnover / eviction cleanout quote. " watch={["#top", "#quote"]} accent={ORANGE} testid="lt-mobile-bar" />
    </div>
  );
}
